import React, { useState } from "react";
import {
    Button,
    Card,
    Table,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    message,
    Tabs,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    EnvironmentOutlined,
    UserOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
    FetchCities_api,
    AddCity_api,
    UpdateCity_api,
    FetchDealers_api,
    AddDealer_api,
    UpdateDealer_api,
    ChangeDealerStatus_api,
} from "../../Api";
import { useAuthChecker } from "../../Context";

const { Option } = Select;

export const ManageDealers = () => {
    const { user } = useAuthChecker();
    const [activeTab, setActiveTab] = useState("1");

    // City State
    const [cityModalOpen, setCityModalOpen] = useState(false);
    const [editingCity, setEditingCity] = useState<any>(null);
    const [cityForm] = Form.useForm();

    // Dealer State
    const [dealerModalOpen, setDealerModalOpen] = useState(false);
    const [editingDealer, setEditingDealer] = useState<any>(null);
    const [dealerForm] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // Pagination & Search
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState("");

    // Queries
    const { data: cityData, refetch: refetchCities, isFetching: isFetchingCities } = useQuery({
        queryKey: ["cities"],
        queryFn: FetchCities_api,
    });

    const { data: dealerData, refetch: refetchDealers, isFetching: isFetchingDealers } = useQuery({
        queryKey: ["dealers", page, pageSize, searchText],
        queryFn: () => FetchDealers_api(page, pageSize, searchText),

    });

    // City Handlers
    const handleAddCity = () => {
        setEditingCity(null);
        cityForm.resetFields();
        setCityModalOpen(true);
    };

    const handleEditCity = (record: any) => {
        setEditingCity(record);
        cityForm.setFieldsValue(record);
        setCityModalOpen(true);
    };

    const handleCitySubmit = async (values: any) => {
        setSubmitting(true);
        try {
            const payload = { ...values, id: editingCity?.id, userId: user?.userId };
            const res = editingCity ? await UpdateCity_api(payload) : await AddCity_api(payload);
            if (res.isDataAdded || res.isDataUpdated) {
                message.success(res.msg);
                setCityModalOpen(false);
                refetchCities();
            } else {
                message.error(res.msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Dealer Handlers
    const handleAddDealer = () => {
        setEditingDealer(null);
        dealerForm.resetFields();
        setDealerModalOpen(true);
    };

    const handleEditDealer = (record: any) => {
        setEditingDealer(record);
        dealerForm.setFieldsValue(record);
        setDealerModalOpen(true);
    };

    const handleDealerSubmit = async (values: any) => {
        setSubmitting(true);
        try {
            const payload = { ...values, id: editingDealer?.id, userId: user?.userId };
            const res = editingDealer ? await UpdateDealer_api(payload) : await AddDealer_api(payload);
            if (res.isDataAdded || res.isDataUpdated) {
                message.success(res.msg);
                setDealerModalOpen(false);
                refetchDealers();
            } else {
                message.error(res.msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDealerStatusChange = async (record: any, checked: boolean) => {
        try {
            const res = await ChangeDealerStatus_api({
                id: record.id,
                status: checked ? 1 : 0,
                userId: user?.userId,
            });
            if (res.isDataUpdated) {
                message.success(res.msg);
                refetchDealers();
            } else {
                message.error(res.msg);
            }
        } catch (error) {
            message.error("Failed to update status");
        }
    };

    const cityColumns = [
        { title: "ID", dataIndex: "id", key: "id", width: 80 },
        { title: "City Name", dataIndex: "city", key: "city" },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (record: any) => (
                <Button size="small" icon={<EditOutlined />} onClick={() => handleEditCity(record)}>
                    Edit
                </Button>
            ),
        },
    ];

    const dealerColumns = [
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "Contact", dataIndex: "contact_number", key: "contact_number" },
        { title: "Email", dataIndex: "email", key: "email" },
        { title: "City", dataIndex: "city_name", key: "city_name" },
        {
            title: "Status",
            key: "status",
            width: 100,
            render: (record: any) => (
                <Switch
                    size="small"
                    checked={record.is_active == 1}
                    onChange={(checked) => handleDealerStatusChange(record, checked)}
                />
            ),
        },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (record: any) => (
                <Button size="small" icon={<EditOutlined />} onClick={() => handleEditDealer(record)}>
                    Edit
                </Button>
            ),
        },
    ];

    const items = [
        {
            key: "1",
            label: (
                <span>
                    <UserOutlined /> Dealers
                </span>
            ),
            children: (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-4 items-center">
                            <h2 className="text-xl font-semibold text-gray-800">Dealer Directory</h2>
                            <Input
                                placeholder="Search dealers..."
                                prefix={<SearchOutlined />}
                                className="w-64"
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                    setPage(1);
                                }}
                            />
                        </div>

                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddDealer}>
                            Add Dealer
                        </Button>
                    </div>
                    <Card className="shadow-sm">
                        <Table
                            className="overflow-x-auto"
                            loading={isFetchingDealers}
                            dataSource={dealerData?.dealerData || []}
                            columns={dealerColumns}
                            rowKey="id"
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: dealerData?.total || 0,
                                onChange: (p, ps) => {
                                    setPage(p);
                                    setPageSize(ps);
                                },
                                showSizeChanger: true
                            }}
                        />
                    </Card>
                </div>
            ),
        },
        {
            key: "2",
            label: (
                <span>
                    <EnvironmentOutlined /> Cities
                </span>
            ),
            children: (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-800">Cities Management</h2>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCity}>
                            Add City
                        </Button>
                    </div>
                    <Card className="shadow-sm max-w-2xl">
                        <Table
                            className="overflow-x-auto"
                            loading={isFetchingCities}
                            dataSource={cityData?.cityData || []}
                            columns={cityColumns}
                            rowKey="id"
                            pagination={{ pageSize: 10 }}
                        />
                    </Card>
                </div>
            ),
        },
    ];

    return (
        <div className="w-full min-h-screen bg-gray-50 p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Manage Dealers & Cities</h1>
            </div>

            <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} type="card" className="bg-white p-4 rounded-xl shadow-sm" />

            {/* City Modal */}
            <Modal
                title={editingCity ? "Edit City" : "Add New City"}
                open={cityModalOpen}
                onCancel={() => setCityModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={cityForm} layout="vertical" onFinish={handleCitySubmit}>
                    <Form.Item
                        label="City Name"
                        name="city"
                        rules={[{ required: true, message: "Please enter city name" }]}
                    >
                        <Input placeholder="e.g. Colombo" />
                    </Form.Item>
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setCityModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={submitting}>
                            {editingCity ? "Update" : "Save"}
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* Dealer Modal */}
            <Modal
                title={editingDealer ? "Edit Dealer" : "Add New Dealer"}
                open={dealerModalOpen}
                onCancel={() => setDealerModalOpen(false)}
                footer={null}
                destroyOnClose
                width={600}
            >
                <Form form={dealerForm} layout="vertical" onFinish={handleDealerSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            label="Dealer Name"
                            name="name"
                            rules={[{ required: true, message: "Please enter dealer name" }]}
                        >
                            <Input placeholder="Full Name" />
                        </Form.Item>
                        <Form.Item
                            label="Contact Number"
                            name="contact_number"
                            rules={[
                                { required: true, message: "Please enter contact number" },
                                { pattern: /^[0-9]{10}$/, message: "Contact number must be 10 digits" }
                            ]}
                        >
                            <Input placeholder="07xxxxxxxx" />
                        </Form.Item>
                        <Form.Item
                            label="Email Address"
                            name="email"
                            rules={[
                                { type: "email", message: "Invalid email format" },
                                { required: true, message: "Please enter email" }
                            ]}
                        >
                            <Input placeholder="email@example.com" />
                        </Form.Item>
                        <Form.Item
                            label="City"
                            name="city_id"
                            rules={[{ required: true, message: "Please select a city" }]}
                        >
                            <Select placeholder="Select city" showSearch optionFilterProp="children">
                                {cityData?.cityData?.map((city: any) => (
                                    <Option key={city.id} value={city.id}>
                                        {city.city}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                        <Button onClick={() => setDealerModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={submitting}>
                            {editingDealer ? "Update Dealer" : "Save Dealer"}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};